
import { CrudFilters, IResourceComponentsProps, useShow, useNavigation, usePermissions, useList } from "@pankod/refine-core";
import {
    List,
    Table,
    TextField,
    useTable,
    getDefaultSortOrder,
    Space,
    EditButton,
    DeleteButton,
    ShowButton,
    Typography,
    Form,
    Icons,
    Col,
    Row,
    Button,
    Input,
    Tooltip,
    CreateButton,
    notification,
    Tag,
    Select,
    Modal,
} from "@pankod/refine-antd";
import { FilePdfOutlined, CalendarOutlined, EyeOutlined, UploadOutlined, CheckOutlined } from "@ant-design/icons";
import { IWorkPlan, WorkPlanStatusEnum } from "interfaces/work-plan.interface";
import { useState } from "react";
import { statusOptions, impactAxes } from "../../constants/workPlan.constants";
import { impactAxisIcons } from "../../constants/impactAxisIcons";
import { WorkPlanDetailModal } from "components/molecules/work-plan-detail-modal";
import { WorkPlanReviewModal } from "components/molecules/work-plan-review-modal";
import { WorkPlanEvidenceModal } from "components/molecules/work-plan-evidence-modal";
import { WorkPlanFinishModal } from "components/molecules/work-plan-finish-modal";
import { WorkPlanProgressStats } from "components/organisms/work-plan-progress-stats";
import { axiosWithAuth } from "utils/axiosWithAuth";
import { API_URL } from "constants/environment";

const { Text } = Typography;

const impactAxisColors: { [key: number]: string } = {
  1: "#87CEEB",      // Azul celeste
  2: "#9932CC",    // Morado
  3: "#FFA500", // Naranja
  4: "#20B2AA",         // Verde azulado/turquesa
  5: "#2F4F4F",                   // Gris oscuro/antracita
  6: "#228B22",           // Verde institucional
};

export const WorkPlanList: React.FC<IResourceComponentsProps> = () => {
    const { push } = useNavigation();
    const { data: permissionsData } = usePermissions();
    
    const isAdmin = permissionsData?.includes('work-plans:admin');

    // Obtener las posiciones para el filtro (solo para admin)
    const { data: positionsData } = useList({
        resource: "work-plans/positions",
        config: {
            hasPagination: false,
        },
        queryOptions: {
            enabled: isAdmin,
        },
    });

    const positions = positionsData?.data || [];
    
    const { tableProps, sorter, searchFormProps, filters, setFilters } = useTable<IWorkPlan>({
        initialSorter: [
            {
                field: "id",
                order: "asc",
            },
        ],
        initialPageSize: 15,
        onSearch: (params: any) => {
            const filters: CrudFilters = [];
            const { q, work_plan_position_id } = params;

            if (q) {
                filters.push({
                    field: "search",
                    operator: "eq",
                    value: q,
                });
            }
            
            if (work_plan_position_id) {
                filters.push({
                    field: "work_plan_position_id",
                    operator: "eq",
                    value: work_plan_position_id,
                });
            }
            
            return filters;
        },
    });

    // Obtener todos los work plans para las estadísticas (sin paginación)
    const { data: allWorkPlansData } = useList<IWorkPlan>({
        resource: "work-plans",
        config: {
            hasPagination: false,
            filters,
        },
    });
    
    const [visible, setVisible] = useState(false);
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [evidenceModalVisible, setEvidenceModalVisible] = useState(false);
    const [finishModalVisible, setFinishModalVisible] = useState(false);
    const [budgetItemsModalVisible, setBudgetItemsModalVisible] = useState(false);
    const [selectedWorkPlan, setSelectedWorkPlan] = useState<IWorkPlan | undefined>();
    const [isUpdating, setIsUpdating] = useState(false);
    const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
    const [isFinishing, setIsFinishing] = useState(false);
    
    const { queryResult, showId, setShowId } = useShow<IWorkPlan>();
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const handleReview = (workPlan: IWorkPlan) => {
        setSelectedWorkPlan(workPlan);
        setReviewModalVisible(true);
    };

    const handleViewBudgetItems = (workPlan: IWorkPlan) => {
        setSelectedWorkPlan(workPlan);
        setBudgetItemsModalVisible(true);
    };

    const handleUploadEvidence = (workPlan: IWorkPlan) => {
        setSelectedWorkPlan(workPlan);
        setEvidenceModalVisible(true);
    };

    const handleFinishWorkPlan = (workPlan: IWorkPlan) => {
        setSelectedWorkPlan(workPlan);
        setFinishModalVisible(true);
    };

    const handleFinishConfirm = async () => {
        if (!selectedWorkPlan) return;

        setIsFinishing(true);
        try {
            const axios = axiosWithAuth();
            await axios.patch(`${API_URL}/work-plans/${selectedWorkPlan.id}/finish`);

            notification.success({
                message: 'Plan de trabajo finalizado exitosamente',
                description: 'El plan de trabajo ha sido marcado como finalizado.',
            });
            
            setFinishModalVisible(false);
            setSelectedWorkPlan(undefined);
            
            // Refresh the table data
            if (tableProps.dataSource) {
                window.location.reload();
            }
        } catch (error) {
            console.error('Error al finalizar el plan de trabajo:', error);
            notification.error({
                message: 'Error',
                description: 'Ocurrió un error al finalizar el plan de trabajo',
            });
        } finally {
            setIsFinishing(false);
        }
    };

    const handleEvidenceUpload = async (file: File) => {
        if (!selectedWorkPlan) return;

        setIsUploadingEvidence(true);
        try {
            const formData = new FormData();
            formData.append('evidence', file);

            const axios = axiosWithAuth();
            await axios.patch(`${API_URL}/work-plans/${selectedWorkPlan.id}/evidence`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            notification.success({
                message: 'Evidencia subida exitosamente',
                description: 'La evidencia se ha subido correctamente.',
            });
            
            setEvidenceModalVisible(false);
            setSelectedWorkPlan(undefined);
            
            // Refresh the table data
            if (tableProps.dataSource) {
                window.location.reload();
            }
        } catch (error) {
            console.error('Error al subir la evidencia:', error);
            notification.error({
                message: 'Error',
                description: 'Ocurrió un error al subir la evidencia',
            });
        } finally {
            setIsUploadingEvidence(false);
        }
    };

    const handleReviewAction = async (action: 'OBSERVATION' | 'AUTHORIZED', observation?: string) => {
        if (!selectedWorkPlan) return;

        setIsUpdating(true);
        try {
            const payload: any = {
                type: action === 'OBSERVATION' ? 1 : 2,
            };

            if (action === 'OBSERVATION' && observation) {
                payload.observation = observation;
            }

            const axios = axiosWithAuth();
            await axios.patch(`${API_URL}/work-plans/${selectedWorkPlan.id}/review`, payload);

            notification.success({
                message: action === 'OBSERVATION' 
                    ? 'Observaciones enviadas exitosamente' 
                    : 'Plan de trabajo autorizado exitosamente',
            });
            
            setReviewModalVisible(false);
            setSelectedWorkPlan(undefined);
            
            if (tableProps.dataSource) {
                window.location.reload();
            }
        } catch (error) {
            console.error('Error al procesar la revisión:', error);
            notification.error({
                message: 'Error',
                description: 'Ocurrió un error al procesar la revisión',
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const getBudgetItems = (workPlan?: IWorkPlan) => {
        if (!workPlan?.budget_items) return [];
        
        // Si budget_items es un array, devolverlo directamente
        if (Array.isArray(workPlan.budget_items)) {
            return workPlan.budget_items;
        }
        
        // Si es un string, intentar parsearlo como JSON
        if (typeof workPlan.budget_items === 'string') {
            try {
                return JSON.parse(workPlan.budget_items);
            } catch (e) {
                console.error('Error parsing budget_items:', e);
                return [];
            }
        }
        
        return [];
    };

    return (
        <>
            {/* Componente de estadísticas de progreso - solo para no admin */}
            {!isAdmin && (
                <WorkPlanProgressStats 
                    workPlans={allWorkPlansData?.data || []}
                    filterByPosition={
                        isAdmin && filters 
                            ? filters.find((f: any) => f.field === 'work_plan_position_id')?.value 
                            : undefined
                    }
                />
            )}
            
            <List
                headerButtons={({ defaultButtons }) => ([
                    <CreateButton key="create" children="Nueva actividad"/>,
                    <Button
                        key="calendarView"
                        type="default"
                        icon={<CalendarOutlined />}
                        onClick={() => push('/work-plans/calendar')}
                    >
                        Vista Calendario
                    </Button>
                ])}
                title={"Actividades"}
            >
                <Form layout="vertical" {...searchFormProps}>
                    <Row gutter={[20, 0]}>
                        <Col span={isAdmin ? 8 : 12}>
                            <Form.Item name="q">
                                <Input
                                    placeholder="Buscar actividad"
                                    prefix={<Icons.SearchOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        {isAdmin && (
                            <Col span={8}>
                                <Form.Item name="work_plan_position_id">
                                    <Select
                                        placeholder="Filtrar por posición"
                                        allowClear
                                        onChange={() => {
                                            searchFormProps.form?.submit();
                                        }}
                                        options={positions.map((position: any) => ({
                                            label: position.name,
                                            value: position.id,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={isAdmin ? 8 : 12}>
                            <Form.Item>
                                <Space>
                                    <Button htmlType="submit" type="primary">
                                        Buscar
                                    </Button>
                                    <Button 
                                        onClick={() => {
                                            searchFormProps.form?.resetFields();
                                            setFilters([], 'replace');
                                        }}
                                    >
                                        Limpiar
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Table {...tableProps} rowKey="id">
                    <Table.Column
                        dataIndex="id"
                        key="id"
                        title="ID"
                        render={(value) => <TextField value={value} />}
                        defaultSortOrder={getDefaultSortOrder("id", sorter)}
                        sorter
                    />
                    <Table.Column
                        dataIndex="impact_axis"
                        key="impact_axis"
                        title="Eje"
                        render={(value) => {
                            const axis = impactAxes.find((a: { id: number; name: string }) => a.id == value);
                            const icon = impactAxisIcons[value] || "📋";
                            const color = impactAxisColors[value] || '#1890ff';
                            const axisName = axis ? axis.name : value.toString();
                            const [tagPart, ...restParts] = axisName.split(':');
                            const rest = restParts.join(':').trim();
                            return <><Tag color={color}>{icon} {tagPart}</Tag> {rest}</>;
                        }}
                        sorter
                    />
                    <Table.Column
                        dataIndex="start_date"
                        key="start_date"
                        title="Fecha Inicio"
                        render={(value) => <TextField value={value} />}
                        sorter
                    />
                    <Table.Column
                        dataIndex="start_time"
                        key="start_time"
                        title="Hora Inicio"
                        render={(value) => <TextField value={value} />}
                        sorter
                    />
                    <Table.Column
                        dataIndex="end_date"
                        key="end_date"
                        title="Fecha Fin"
                        render={(value) => <TextField value={value} />}
                        sorter
                    />
                    <Table.Column
                        dataIndex="end_time"
                        key="end_time"
                        title="Hora Fin"
                        render={(value) => <TextField value={value} />}
                        sorter
                    />
                    <Table.Column
                        dataIndex={["work_plan_position", "name"]}
                        key="work_plan_position.name"
                        title="Unidad Organizacional"
                        render={(value) => {
                            if (!value) {
                                return <Tag color="orange">Sin asignar</Tag>;
                            }
                            return <TextField value={value} />;
                        }}
                    />
                    <Table.Column
                        dataIndex="general_objective"
                        key="general_objective"
                        title="Objetivo general"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="goal"
                        key="goal"
                        title="Meta"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="strategy"
                        key="strategy"
                        title="Estrategia"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="audience"
                        key="audience"
                        title="Público"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="scope"
                        key="scope"
                        title="Alcance"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column<IWorkPlan>
                        dataIndex="budget_url"
                        key="budget_url"
                        title="Presupuesto"
                        render={(_, record: IWorkPlan) => (
                            <Space>
                                {record.budget_url ? (
                                    <Tooltip title="Ver PDF">
                                        <a href={record.budget_url} target="_blank" rel="noopener noreferrer">
                                            <FilePdfOutlined style={{ fontSize: 20, color: '#d32f2f' }} />
                                        </a>
                                    </Tooltip>
                                ) : (
                                    getBudgetItems(record).length > 0 ? (
                                        <Tooltip title="Ver insumos">
                                            <Button
                                                type="link"
                                                icon={<Icons.UnorderedListOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                                                onClick={() => handleViewBudgetItems(record)}
                                            />
                                        </Tooltip>
                                    ) : null
                                )}
                            </Space>
                        )}
                    />
                    <Table.Column
                        dataIndex="support_budget"
                        key="support_budget"
                        title="Apoyo Vicerrectoría"
                        render={(value) => (
                            <TextField value={value ? `$${Number(value)?.toLocaleString()}` : '$0'} />
                        )}
                    />
                    <Table.Column
                        dataIndex="total_budget"
                        key="total_budget"
                        title="Total presupuesto"
                        render={(value) => (
                            <TextField value={`$${Number(value)?.toLocaleString()}`} />
                        )}
                    />
                    <Table.Column
                        dataIndex="status"
                        key="status"
                        title="Estatus"
                        render={(value) => <TextField value={statusOptions[value] || value} />}
                        sorter  
                    />
                    <Table.Column<IWorkPlan>
                        title="Actions"
                        dataIndex="actions"
                        render={(_, record) => (
                            <Space>
                                { (isAdmin || [WorkPlanStatusEnum.SUBMITTED, WorkPlanStatusEnum.OBSERVATION].includes(record.status)) && (
                                    <EditButton hideText size="small" recordItemId={record.id} />
                                )}
                                <ShowButton
                                    hideText
                                    size="small"
                                    recordItemId={record.id}
                                    onClick={() => {
                                        setShowId(record.id);
                                        setVisible(true);
                                    }}
                                />
                                {isAdmin && [WorkPlanStatusEnum.SUBMITTED, WorkPlanStatusEnum.RESOLVED].includes(record.status) && (
                                    <Tooltip title="Revisar plan de trabajo">
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => handleReview(record)}
                                        >
                                            Revisar
                                        </Button>
                                    </Tooltip>
                                )}
                                {record.status === WorkPlanStatusEnum.AUTHORIZED && (
                                    <Tooltip title="Subir evidencia">
                                        <Button
                                            type="default"
                                            size="small"
                                            icon={<UploadOutlined />}
                                            onClick={() => handleUploadEvidence(record)}
                                        >
                                            Subir Evidencia
                                        </Button>
                                    </Tooltip>
                                )}
                                {isAdmin && record.status === WorkPlanStatusEnum.DONE && (
                                    <Tooltip title="Finalizar plan de trabajo">
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<CheckOutlined />}
                                            onClick={() => handleFinishWorkPlan(record)}
                                        >
                                            Finalizar
                                        </Button>
                                    </Tooltip>
                                )}
                                { (isAdmin || [WorkPlanStatusEnum.SUBMITTED].includes(record.status)) && (
                                    <DeleteButton hideText size="small" recordItemId={record.id} />
                                )}
                            </Space>
                        )}
                    />
                </Table>
            </List>
            <WorkPlanDetailModal
                visible={visible}
                onCancel={() => setVisible(false)}
                onOk={() => setVisible(false)}
                isLoading={isLoading}
                showId={showId || ""}
                showRecord={record}
                hasEditPermission={true}
            />
            <WorkPlanReviewModal
                visible={reviewModalVisible}
                onCancel={() => {
                    setReviewModalVisible(false);
                    setSelectedWorkPlan(undefined);
                }}
                onReview={handleReviewAction}
                workPlan={selectedWorkPlan}
                isLoading={isUpdating}
            />
            <WorkPlanEvidenceModal
                visible={evidenceModalVisible}
                onCancel={() => {
                    setEvidenceModalVisible(false);
                    setSelectedWorkPlan(undefined);
                }}
                onUpload={handleEvidenceUpload}
                workPlan={selectedWorkPlan}
                isLoading={isUploadingEvidence}
            />
            <WorkPlanFinishModal
                visible={finishModalVisible}
                onCancel={() => {
                    setFinishModalVisible(false);
                    setSelectedWorkPlan(undefined);
                }}
                onConfirm={handleFinishConfirm}
                workPlan={selectedWorkPlan}
                isLoading={isFinishing}
            />
            <Modal
                title="Insumos del Presupuesto"
                visible={budgetItemsModalVisible}
                onCancel={() => {
                    setBudgetItemsModalVisible(false);
                    setSelectedWorkPlan(undefined);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setBudgetItemsModalVisible(false);
                        setSelectedWorkPlan(undefined);
                    }}>
                        Cerrar
                    </Button>
                ]}
                width={1000}
            >
                {(() => {
                    const budgetItems = getBudgetItems(selectedWorkPlan);
                    return budgetItems.length > 0 ? (
                        <>
                            <Table
                                dataSource={budgetItems}
                                pagination={false}
                                bordered
                                size="small"
                                scroll={{ x: 800 }}
                                summary={(data) => {
                                    const totalWithoutSupport = data
                                        .filter((item: any) => !item.requires_support)
                                        .reduce((sum, item: any) => sum + Number(item.total_cost || 0), 0);
                                    const totalWithSupport = data
                                        .filter((item: any) => item.requires_support)
                                        .reduce((sum, item: any) => sum + Number(item.total_cost || 0), 0);
                                    
                                    return (
                                        <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                            <Table.Summary.Cell index={0} colSpan={3} align="right">
                                                <Text strong style={{ fontSize: '16px' }}>Totales:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="center">
                                                <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                                                    ${totalWithoutSupport.toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="center">
                                                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                                    ${totalWithSupport.toFixed(2)}
                                                </Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    );
                                }}
                            >
                                <Table.Column
                                    title="Cantidad"
                                    dataIndex="quantity"
                                    key="quantity"
                                    width={100}
                                    align="center"
                                    render={(value) => <TextField value={Math.round(value)} />}
                                />
                                <Table.Column
                                    title="Insumo"
                                    dataIndex="supply_name"
                                    key="supply_name"
                                    width={250}
                                    render={(value) => <TextField value={value} />}
                                />
                                <Table.Column
                                    title="Costo Unitario"
                                    dataIndex="unit_cost"
                                    key="unit_cost"
                                    width={150}
                                    align="center"
                                    render={(value) => (
                                        <TextField value={`$${Number(value).toFixed(2)}`} />
                                    )}
                                />
                                <Table.Column
                                    title="Cubierto por UO"
                                    dataIndex="requires_support"
                                    key="covered_by_uo"
                                    width={180}
                                    align="center"
                                    render={(value, record: any) => (
                                        !value ? (
                                            <Text strong style={{ color: '#52c41a' }}>
                                                ${Number(record.total_cost).toFixed(2)}
                                            </Text>
                                        ) : (
                                            <Text type="secondary">---</Text>
                                        )
                                    )}
                                />
                                <Table.Column
                                    title="Solicitado a Vicerrectoría"
                                    dataIndex="requires_support"
                                    key="requires_support"
                                    width={180}
                                    align="center"
                                    render={(value, record: any) => (
                                        value ? (
                                            <Text strong style={{ color: '#1890ff' }}>
                                                ${Number(record.total_cost).toFixed(2)}
                                            </Text>
                                        ) : (
                                            <Text type="secondary">---</Text>
                                        )
                                    )}
                                />
                            </Table>
                            <div style={{ 
                                marginTop: '24px', 
                                padding: '16px 24px', 
                                backgroundColor: '#f0f5ff', 
                                borderRadius: '8px',
                                border: '2px solid #1890ff',
                                textAlign: 'center'
                            }}>
                                <Text style={{ fontSize: '18px', marginRight: '12px' }}>
                                    TOTAL PRESUPUESTO:
                                </Text>
                                <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                                    ${budgetItems.reduce((sum: number, item: any) => sum + Number(item.total_cost || 0), 0).toFixed(2)}
                                </Text>
                            </div>
                        </>
                    ) : (
                        <Text>No hay insumos registrados para este plan de trabajo.</Text>
                    );
                })()}
            </Modal>
        </>
    );
};

export default WorkPlanList;
